/* © CNES */
import { useQueries, UseQueryResult } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import moment from 'moment';
import * as ol from 'ol';
import { Coordinate } from 'ol/coordinate';
import { pointerMove } from 'ol/events/condition';
import { Extent } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON.js';
import Polygon, { fromExtent } from 'ol/geom/Polygon';
import Draw, { createBox } from 'ol/interaction/Draw.js';
import Select from 'ol/interaction/Select.js';
import { Vector as VectorLayer } from 'ol/layer.js';
import BaseLayer from 'ol/layer/Base';
import TileWMS from 'ol/source/TileWMS';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getFeatureInfo } from '../../api/geoserver/GetFeatureInfo';
import { ConfigContext } from '../../context/ConfigContext';
import { useFormattedCollections } from '../../hooks/CustomHooks';
import { CollectionDetails } from '../../models/CollectionDetails';
import { GeoServerFeature } from '../../models/GeoserverFeature';
import './Map.scss';
import MapContext from './MapContext';

/**
 * Map openLayers map functionnalities.
 * @param children Layers to display.
 * @param zoom zoom on map.
 * @param center center of map.
 * @param isInDrawMode can user draw on map.
 * @param setIsInDrawMode Function to set draw mode.
 * @param tileSourceTable tileSource Table or geoserver layers.
 * @param time time parameter for geoserver layers.
 * @param isFiltersVisible isFiltersVisible parameter to handle layer visibility.
 * @returns map
 */
const Map = ({
  children,
  zoom,
  center,
  isInDrawMode,
  setIsInDrawMode,
  tileSourceTable,
  time,
  isFiltersVisible,
}: {
  children: any;
  zoom: number | undefined;
  center: Coordinate | undefined;
  isInDrawMode: boolean;
  setIsInDrawMode: Function;
  tileSourceTable: { tile: TileWMS; showLayer: boolean }[];
  time: () => string;
  isFiltersVisible: boolean;
}) => {
  const context = useContext(ConfigContext);
  const mapRef = useRef<any>();
  const [map, setMap] = useState(new ol.Map());
  const sourceDrawInteractions = new VectorSource({ wrapX: false });
  const sourceFeatures = new VectorSource({ wrapX: false });
  const [extent, setExtent] = useState<Number[] | null>(null);

  const formattedCollectionsList: CollectionDetails[] = useFormattedCollections(
    context.searchedFeatures,
    context.itemsAvailabilities
  );

  /**
   * select Style from feature.
   */
  const selectStyle = useCallback((feature: any) => {
    const selected = new Style({
      fill: new Fill({
        color: 'rgba(255, 255, 255, 0)',
      }),
      stroke: new Stroke({
        color: '#ffa500',
        width: 3,
      }),
    });
    return selected;
  }, []);

  // select interaction working on "pointermove"
  const selectPointerMove = useMemo(
    () =>
      new Select({
        condition: pointerMove,
        style: selectStyle,
      }),
    [selectStyle]
  );

  // Vector Layer for draw interactions.
  const vectorDrawInteractions = new VectorLayer({
    properties: { name: 'vectorDrawInteractions' },
    source: sourceDrawInteractions,
    style: {
      'fill-color': 'rgba(255, 255, 255, 0.2)',
      'stroke-color': '#ffcc33',
      'stroke-width': 2,
      'circle-radius': 7,
      'circle-fill-color': '#ffcc33',
    },
    zIndex: 9999,
  });
  // Vector Layer for feature clicked by user.
  const vectorFeatures = new VectorLayer({
    properties: { name: 'vectorFeatures' },
    source: sourceFeatures,
    style: {
      'fill-color': 'rgba(255, 255, 255, 0.2)',
      'stroke-color': '#ffa500',
      'stroke-width': 3,
      'circle-radius': 7,
      'circle-fill-color': '#ffa500',
    },
    zIndex: 9999,
  });

  /**
   * called to remove a specific layer from map
   * @param layerToRemove
   */
  const removeLayer = (layerToRemove: BaseLayer | undefined) => {
    if (layerToRemove) map.removeLayer(layerToRemove);
  };

  useEffect(() => {
    const layerToRemove = map
      .getLayers()
      .getArray()
      .find(
        (layer: BaseLayer) =>
          layer.get('name') && layer.get('name') === 'geoJson_file'
      );

    if (context.searchZone.value) {
      removeLayer(layerToRemove);
      const vectorSource = new VectorSource({
        features: new GeoJSON().readFeatures(context.searchZone.value, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857',
        }),
      });
      const feature = vectorSource.getFeatures()[0]?.getGeometry();

      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: {
          'fill-color': 'rgba(255, 255, 255, 0.2)',
          'stroke-color': '#ffcc33',
          'stroke-width': 2,
        },
        zIndex: 9999,
      });
      vectorLayer.set('name', 'geoJson_file');
      map.addLayer(vectorLayer);
      if (feature) {
        map.getView().fit(feature.getExtent(), { duration: 1000 });
      }
    } else {
      removeLayer(layerToRemove);
    }
    // eslint-disable-next-line
  }, [context.searchZone, map]);

  useEffect(() => {
    if (extent && isFiltersVisible) {
      context.setMapExtent({
        extent: map.getView().calculateExtent(),
        currentView: context.mapExtent.currentView,
        bbox: map.getView().calculateExtent(),
      });
    }
    // eslint-disable-next-line
  }, [extent, isFiltersVisible]);

  const draw = new Draw({
    source: sourceDrawInteractions,
    type: 'Circle',
    geometryFunction: createBox(),
  });
  const view = useMemo(() => new ol.View({ zoom, center }), [center, zoom]);
  const [featureUrls, setFeatureUrls] = useState<any[]>([]);
  const [lastDrawnShape, setLastDrawnShape] = useState<Extent | undefined>(
    undefined
  );
  // Queries to obtain featureInfo on click.
  const [...featuresFromQueries] = useQueries({
    queries: featureUrls,
  });

  // on component mount
  useEffect(() => {
    let options = {
      view,
      layers: [],
      controls: [],
      overlays: [],
    };

    let mapObject = new ol.Map(options);
    mapObject.setTarget(mapRef.current);
    setMap(mapObject);

    return () => mapObject.setTarget();
    // eslint-disable-next-line
  }, []);

  /**
   * Add a polygon to the map
   * Open right panel with selected Feature
   * When features are found in one of the geoserver layers (s1, s2l1c, s2l2a)
   * On user click interaction on map.
   */
  useEffect(() => {
    if (isEqual(prevDataFeatures.current, featuresFromQueries)) {
      return;
    }

    if (isFiltersVisible) {
      return;
    }

    if (featuresFromQueries?.length > 0) {
      // get the most recent feature on clicked location or the first one with geometry.

      const jsonFeatures = [...featuresFromQueries]
        .map(
          (featureClicked) =>
            (featureClicked.data as { features: GeoServerFeature[] })
              ?.features ?? []
        )
        .flat();

      const jsonFeature = jsonFeatures.reduce(
        (acc: GeoServerFeature | undefined, curr: GeoServerFeature) => {
          if (
            moment(curr?.properties?.acquisition).isAfter(
              acc?.properties?.acquisition
            ) &&
            curr?.geometry
          ) {
            acc = curr;
          }
          return acc;
        },
        jsonFeatures.find((feature) => feature.geometry)
      );

      if (!sourceFeatures.getFeatures().length && jsonFeature?.geometry) {
        let feature = new ol.Feature({
          geometry: new Polygon(jsonFeature?.geometry?.coordinates ?? []),
          name: jsonFeature?.properties?.name,
          image: jsonFeature?.properties?.image,
        });

        showMetaDataFromFeature(feature);
        mapRefreshLayer();

        if (map.getLayers().getArray()) {
          const layerToRemove = map
            .getAllLayers()
            .find((layer) => layer.getProperties().name === 'vectorFeatures');
          if (layerToRemove) map.removeLayer(layerToRemove);

          map.addLayer(vectorFeatures);
        }
      }
    }
    // eslint-disable-next-line
  }, [featuresFromQueries]);

  /**
   * create a ref for previous dataFeatures to use for deep compare
   */
  const prevDataFeatures = useRef<UseQueryResult<unknown, unknown>[]>();
  useEffect(() => {
    prevDataFeatures.current = featuresFromQueries;
  });

  /**
   * Refresh vectorFeatures Layer on map.
   */
  const mapRefreshLayer = () => {
    const layerToRemove = map
      .getLayers()
      .getArray()
      .filter(
        (layer: BaseLayer) =>
          layer.get('name') &&
          layer.get('name') !== 'geoJson_file' &&
          layer.get('name') !== 'vectorDrawInteractions'
      );

    layerToRemove.forEach((layer) => removeLayer(layer));
    map.addLayer(vectorFeatures);
  };

  /**
   * show MetaData From Feature after user clicked on map.
   * @param jsonFeature the best fitting feature on click coordinates.
   */
  const showMetaDataFromFeature = (feature: ol.Feature) => {
    sourceFeatures.addFeature(feature);
    context.setRightPanelVisibility(
      true,
      formattedCollectionsList.find(
        (collection) => collection.productName === feature.getProperties().name
      ),
      'Map'
    );
  };

  // remove Layer for clicked features when filter tab is shown or if selectedProduct comes from Results.
  useEffect(() => {
    if (isFiltersVisible || context.selectedProductOrigin === 'Results') {
      const layerToRemove = map
        .getAllLayers()
        .find((layer) => layer.getProperties().name === 'vectorFeatures');
      if (layerToRemove) map.removeLayer(layerToRemove);
    }
    // eslint-disable-next-line
  }, [isFiltersVisible, context.selectedProductOrigin]);

  /**
   * Callback to handle single clicks on map.
   * Active only on Results tab / no dragging allowed.
   * Build getFeatureInfo urls for S1 and S2.
   * @param event ol.MapBrowserEvent.
   * @returns void.
   */
  const onSingleClickCallback = useCallback(
    (event: ol.MapBrowserEvent<any>) => {
      if (event.dragging) {
        return;
      }

      if (isFiltersVisible) {
        return;
      }

      // The server will need the viewResolution, the coordinates, the projection and the format
      const viewResolution = event.map.getView().getResolution() ?? 0;

      setFeatureUrls(
        tileSourceTable
          .filter((tileSource) => tileSource.showLayer)
          .map((tileSource) => {
            return tileSource.tile.getFeatureInfoUrl(
              event.coordinate,
              viewResolution,
              'EPSG:3857',
              {
                INFO_FORMAT: 'application/json',
                time: time(),
              }
            );
          })
          .map((featureUrl) => ({
            queryKey: [featureUrl],
            queryFn: getFeatureInfo,
            enabled: !!featureUrl && !isFiltersVisible,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
            retry: false,
          }))
      );
    },
    [isFiltersVisible, tileSourceTable, time]
  );

  useEffect(() => {
    map.on('singleclick', onSingleClickCallback);
    return () => {
      map.un('singleclick', onSingleClickCallback);
    };
  }, [onSingleClickCallback, map]);

  // disabled currentview when Results view is visble.
  if (isFiltersVisible) {
    map.on('moveend', function (event) {
      setExtent(map.getView().calculateExtent());
    });
  }

  // zoom change handler
  useEffect(() => {
    if (!map) return;
    map.getView().setZoom(zoom ?? 0);
    map.addInteraction(selectPointerMove);
  }, [map, zoom, selectPointerMove]);
  // center change handler
  useEffect(
    () => {
      map.getView().setCenter(center);
      map.getView().fit(context.mapExtent.extent);
      map.getView().setZoom(0);
    },
    // eslint-disable-next-line
    [context.mapExtent.currentView, map]
  );

  useEffect(() => {
    if (map && !isFiltersVisible) {
      const geom = fromExtent(map.getView().getViewStateAndExtent().extent);
      geom.scale(3);
      map.getView().fit(geom, { size: map.getSize() });
      context.setMapExtent({
        extent: context.mapExtent.extent,
        currentView: context.mapExtent.currentView,
        bbox: geom.getExtent(),
      });
    }
    // eslint-disable-next-line
  }, [isFiltersVisible]);

  // add interactions on draw mode.
  useEffect(() => {
    if (isInDrawMode) {
      // set Last Drawn Shape to allow comparison with new shape in case of draw cancel.
      setLastDrawnShape(context.drawnShape);
      const layerToRemove = map
        .getAllLayers()
        .find(
          (layer) => layer.getProperties().name === 'vectorDrawInteractions'
        );
      if (layerToRemove) map.removeLayer(layerToRemove);

      map.addLayer(vectorDrawInteractions);
      map.addInteraction(draw);

      // on draw end, save shape, clear source layer, toggle drawMode.
      draw.on('drawend', function (event) {
        sourceDrawInteractions.clear();
        context.setDrawnShape(event.feature.getGeometry()?.getExtent());
        setIsInDrawMode(false);
      });

      // on draw abort, remove drawnShape tag.
      draw.on('drawabort', function (event) {
        context.setDrawnShape(undefined);
      });
    } else {
      // set drawShape to undefined in case user cancel draw.
      if (lastDrawnShape?.toString() === context.drawnShape?.toString()) {
        context.setDrawnShape(undefined);
      }

      map.removeInteraction(draw);
      map.getInteractions().removeAt(
        map
          .getInteractions()
          .getArray()
          .findIndex((interaction) => interaction instanceof Draw)
      );
    }
    // eslint-disable-next-line
  }, [isInDrawMode]);

  useEffect(() => {
    if (!map) return;

    // Get params from URL
    const urlParams = new URLSearchParams(window.location.search);
    const handDrawnArea = urlParams.get('handDrawnArea');

    if (handDrawnArea) {
      const extentArray = handDrawnArea.split(',').map(Number);
      const rectangle = fromExtent(extentArray);

      if (rectangle) {
        // remove all layers named vectorDrawInteractions.
        const layerToRemove = map
          .getAllLayers()
          .find(
            (layer) => layer.getProperties().name === 'vectorDrawInteractions'
          );
        if (layerToRemove) map.removeLayer(layerToRemove);

        // Ajouter le rectangle à la carte
        const feature = new ol.Feature(rectangle);
        sourceDrawInteractions.addFeature(feature);

        const layer = new VectorLayer({
          source: sourceDrawInteractions,
          style: {
            'fill-color': 'rgba(255, 255, 255, 0.2)',
            'stroke-color': '#ffcc33',
            'stroke-width': 2,
          },
          zIndex: 9999,
        });
        layer.set('name', 'vectorDrawInteractions');
        map.addLayer(layer);
      }
    }
    // eslint-disable-next-line
  }, [map, window.location.search]);

  useEffect(() => {
    // remove all layers named vectorDrawInteractions if no drawn shape.
    if (!context.drawnShape) {
      const layerToRemove = map
        .getAllLayers()
        .find(
          (layer) => layer.getProperties().name === 'vectorDrawInteractions'
        );
      if (layerToRemove) map.removeLayer(layerToRemove);
    }
    // eslint-disable-next-line
  }, [context.drawnShape]);

  const mapValue = useMemo(
    () => ({
      map,
    }),
    [map]
  );

  return (
    <MapContext.Provider value={mapValue}>
      <div ref={mapRef} className="ol-map">
        {children}
      </div>
    </MapContext.Provider>
  );
};
export default Map;
